import { cliente } from '@/lib/cliente';
import type { AbrirSemana, EjecutarCorteA } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Hooks de Fase 4 — semana operativa + cierre.
 *   - useSemanaActual: la semana abierta (o null).
 *   - useCierrePreview: qué pasaría si cerramos ahora.
 *   - useEjecutarCorteA: dispara el cierre semanal (corte A).
 *   - useAbrirSemana: abre una nueva semana (admin).
 */

export function useSemanaActual() {
  return useQuery({
    queryKey: ['semanas', 'actual'],
    queryFn: () => cliente.semanas.actual(),
    staleTime: 30_000,
  });
}

export function useCierrePreview() {
  return useQuery({
    queryKey: ['cierre', 'preview'],
    queryFn: () => cliente.cierre.preview(),
    staleTime: 10_000,
  });
}

export function useEjecutarCorteA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EjecutarCorteA) => cliente.cierre.corteA(body),
    onSuccess: () => {
      // Invalida todo lo que ahora cambió: semana, preview, avance, obras (sprint_actual).
      qc.invalidateQueries({ queryKey: ['semanas'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
      qc.invalidateQueries({ queryKey: ['avance'] });
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['matriz'] });
    },
  });
}

/** Foto B — snapshot del avance actual sin cerrar la semana (no avanza ni pide NC). */
export function useFotoB() {
  return useMutation({
    mutationFn: (fechaCorte?: string) => cliente.cierre.fotoB(fechaCorte),
  });
}

export function useAbrirSemana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AbrirSemana) => cliente.semanas.abrir(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semanas'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
    },
  });
}

/** Fija la línea base de la semana abierta — snapshot del sprint_actual de cada obra. */
export function useFijarLineaBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (semanaId: number) => cliente.semanas.fijarLineaBase(semanaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cierre'] });
      qc.invalidateQueries({ queryKey: ['semanas'] });
    },
  });
}

/**
 * Preview de qué obras se revertirían — para mostrar antes de pedir
 * confirmación. Solo se pide cuando el diálogo de revertir está abierto.
 */
export function useRevertirAvancePreview(semanaId: number | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ['semanas', semanaId, 'revertir-avance-preview'],
    queryFn: () => cliente.semanas.revertirAvancePreview(semanaId!),
    enabled: habilitado && !!semanaId,
    staleTime: 0,
  });
}

/**
 * Revierte a su último cierre el avance de las obras que están bloqueando el
 * re-fijado de línea base (avance por encima de la foto de esta semana).
 * Deja el avance de esta semana en blanco para esas obras — se vuelve a
 * digitar después de re-fijar.
 */
export function useRevertirAvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (semanaId: number) => cliente.semanas.revertirAvance(semanaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cierre'] });
      qc.invalidateQueries({ queryKey: ['avance'] });
      qc.invalidateQueries({ queryKey: ['matriz'] });
    },
  });
}

/** Edita la semana abierta (por ahora: días efectivos / descripción). */
export function useActualizarSemana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      semanaId,
      body,
    }: { semanaId: number; body: { dias_efectivos?: number; descripcion?: string | null } }) =>
      cliente.semanas.actualizar(semanaId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semanas'] });
    },
  });
}
